import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
    ConveyorStationWithCost,
    PredefinedStation,
    RFQ,
    RFQProgramParams,
} from '../types';
import { conveyorStationsApi, quoteApi, rfqApi } from '../services/api';
import CycleGantt from './CycleGantt';
import './RFQReport.css';

export type RFQReportDetail = 'itemized' | 'aggregated';

interface RFQReportProps {
    rfqId: string;
    detail?: RFQReportDetail;
}

const currency = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
});

const formatIssueDate = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toISOString().slice(0, 10);
};

const formatGeneratedDate = (d: Date): string =>
    d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

const RFQReport: React.FC<RFQReportProps> = ({ rfqId, detail = 'itemized' }) => {
    const showPerStationCosts = detail === 'itemized';
    const [rfq, setRfq] = useState<RFQ | null>(null);
    const [stations, setStations] = useState<ConveyorStationWithCost[]>([]);
    const [stationTypes, setStationTypes] = useState<PredefinedStation[]>([]);
    const [params, setParams] = useState<RFQProgramParams>({});
    const [termsMarkdown, setTermsMarkdown] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const [
                    rfqData,
                    stationsData,
                    stationTypesData,
                    paramsData,
                    termsData,
                ] = await Promise.all([
                    rfqApi.get(rfqId),
                    rfqApi.getConveyorStations(rfqId),
                    conveyorStationsApi.listStationTypes(),
                    rfqApi.getProgramParams(rfqId),
                    quoteApi.getTerms(),
                ]);
                setRfq(rfqData);
                setStations(stationsData);
                setStationTypes(stationTypesData);
                setParams(paramsData);
                setTermsMarkdown(termsData);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load report');
            } finally {
                setIsLoading(false);
            }
        };
        void load();
    }, [rfqId]);

    const stationTypeName = useMemo(() => {
        const map = new Map(stationTypes.map((s) => [s.id, s.name]));
        return (id: string) => map.get(id) ?? id;
    }, [stationTypes]);

    const totals = useMemo(() => {
        let goods = 0;
        let labor = 0;
        for (const station of stations) {
            goods += station.total_goods;
            labor += station.total_labor;
        }
        return { goods, labor, grand: goods + labor };
    }, [stations]);

    if (isLoading) {
        return (
            <div className="report-page ff-display">
                <div className="ff-mono report-empty">Loading report…</div>
            </div>
        );
    }

    if (error || !rfq) {
        return (
            <div className="report-page ff-display">
                <div className="ff-mono report-empty">
                    {error ?? 'RFQ not found.'}
                </div>
            </div>
        );
    }

    const generatedAt = formatGeneratedDate(new Date());

    return (
        <>
            <div className="report-toolbar no-print ff-mono">
                <button
                    type="button"
                    onClick={() => window.history.back()}
                    className="ff-mono"
                    style={{
                        fontSize: 10,
                        letterSpacing: '0.15em',
                        textTransform: 'uppercase',
                        padding: '8px 12px',
                        border: '1px solid rgba(20,20,20,0.25)',
                        background: 'white',
                        cursor: 'pointer',
                    }}
                >
                    ← Back
                </button>
                <button
                    type="button"
                    onClick={() => window.print()}
                    style={{
                        fontSize: 10,
                        letterSpacing: '0.15em',
                        textTransform: 'uppercase',
                        padding: '8px 12px',
                        background: '#141414',
                        color: '#f3efe6',
                        border: 'none',
                        cursor: 'pointer',
                    }}
                >
                    Print
                </button>
            </div>

            <div className="report-page ff-display">
                <header className="report-cover">
                    <div>
                        <div className="ff-mono report-cover__eyebrow">
                            ARM-Technologies Quote
                        </div>
                        <h1 className="ff-serif report-cover__title">{rfq.title}</h1>
                        <div className="ff-display report-cover__client">
                            {rfq.client_name}
                        </div>
                        <div className="ff-mono report-cover__meta">
                            <span>
                                Issued: <strong>{formatIssueDate(rfq.created_at)}</strong>
                            </span>
                            <span>
                                Generated: <strong>{generatedAt}</strong>
                            </span>
                            {rfq.part_type && (
                                <span>
                                    Part type: <strong>{rfq.part_type}</strong>
                                </span>
                            )}
                        </div>
                    </div>
                    <img
                        src="/arm.png"
                        alt=""
                        className="report-cover__logo"
                        aria-hidden
                    />
                </header>

                {rfq.description && rfq.description.trim() && (
                    <section className="report-section">
                        <div className="report-section__heading">
                            <div>
                                <div className="ff-mono report-section__eyebrow">
                                    Overview
                                </div>
                                <h2 className="ff-serif report-section__title">
                                    Description
                                </h2>
                            </div>
                        </div>
                        <div className="report-description ff-display">
                            {rfq.description
                                .split(/\n\s*\n/)
                                .map((paragraph, idx) => (
                                    <p key={idx}>{paragraph.trim()}</p>
                                ))}
                        </div>
                    </section>
                )}

                <section className="report-section">
                    <div className="report-section__heading">
                        <div>
                            <div className="ff-mono report-section__eyebrow">
                                Summary
                            </div>
                            <h2 className="ff-serif report-section__title">Costs</h2>
                        </div>
                    </div>
                    <div className="cost-summary">
                        <div className="cost-summary__cell">
                            <div className="ff-mono cost-summary__label">Capital</div>
                            <div className="ff-serif cost-summary__value">
                                {currency.format(totals.goods)}
                            </div>
                        </div>
                        <div className="cost-summary__cell">
                            <div className="ff-mono cost-summary__label">Labor</div>
                            <div className="ff-serif cost-summary__value">
                                {currency.format(totals.labor)}
                            </div>
                        </div>
                        <div className="cost-summary__cell">
                            <div className="ff-mono cost-summary__label">Total</div>
                            <div className="ff-serif cost-summary__value">
                                {currency.format(totals.grand)}
                            </div>
                        </div>
                    </div>
                    <div className="ff-mono cost-summary__counts">
                        {stations.length} station{stations.length === 1 ? '' : 's'}
                    </div>
                </section>

                <section className="report-section">
                    <div className="report-section__heading">
                        <div>
                            <div className="ff-mono report-section__eyebrow">
                                Design
                            </div>
                            <h2 className="ff-serif report-section__title">
                                Conveyor stations
                            </h2>
                        </div>
                    </div>
                    {stations.length === 0 ? (
                        <div className="ff-mono report-empty">
                            No stations configured.
                        </div>
                    ) : (
                        stations.map((station, index) => (
                            <article className="station" key={`station-${index}`}>
                                <div className="station__header">
                                    <div className="ff-mono station__index">
                                        Station {index + 1}
                                    </div>
                                    <h3 className="ff-serif station__title">
                                        {station.name ||
                                            stationTypeName(station.station_type_id)}
                                    </h3>
                                    {station.name && (
                                        <div className="ff-mono station__type">
                                            {stationTypeName(station.station_type_id)}
                                        </div>
                                    )}
                                    {station.comments && (
                                        <div className="ff-display station__comments">
                                            {station.comments}
                                        </div>
                                    )}
                                </div>
                                <div className="station__body">
                                    {station.steps.length > 0 && (
                                        <ol className="ff-display station__steps">
                                            {station.steps.map((step) => (
                                                <li key={step.id}>{step.text}</li>
                                            ))}
                                        </ol>
                                    )}
                                    {station.equipment.length === 0 ? (
                                        <div className="ff-mono report-empty">
                                            No equipment assigned.
                                        </div>
                                    ) : showPerStationCosts ? (
                                        <div className="station-summary">
                                            <div className="station-summary__cell">
                                                <div className="ff-mono station-summary__label">
                                                    Capital
                                                </div>
                                                <div className="ff-serif station-summary__value">
                                                    {currency.format(station.total_goods)}
                                                </div>
                                            </div>
                                            <div className="station-summary__cell">
                                                <div className="ff-mono station-summary__label">
                                                    Labor
                                                </div>
                                                <div className="ff-serif station-summary__value">
                                                    {currency.format(station.total_labor)}
                                                </div>
                                            </div>
                                            <div className="station-summary__cell">
                                                <div className="ff-mono station-summary__label">
                                                    Station total
                                                </div>
                                                <div className="ff-serif station-summary__value">
                                                    {currency.format(station.total)}
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            </article>
                        ))
                    )}
                </section>

                <section className="report-section">
                    <div className="report-section__heading">
                        <div>
                            <div className="ff-mono report-section__eyebrow">
                                Cycle
                            </div>
                            <h2 className="ff-serif report-section__title">
                                Operation Gantt
                            </h2>
                        </div>
                    </div>
                    <CycleGantt
                        stations={stations}
                        taktSeconds={params.takt_seconds ?? null}
                        showCyclesControl={false}
                        initialCycles={1}
                    />
                </section>

                <section className="report-section">
                    <div className="report-section__heading">
                        <div>
                            <div className="ff-mono report-section__eyebrow">
                                Procurement
                            </div>
                            <h2 className="ff-serif report-section__title">
                                Bill of materials
                            </h2>
                        </div>
                    </div>
                    <div className="bom-summary">
                        <div className="bom-summary__cell">
                            <div className="ff-mono bom-summary__label">Capital total</div>
                            <div className="ff-serif bom-summary__value">
                                {currency.format(totals.goods)}
                            </div>
                        </div>
                    </div>
                </section>

                {termsMarkdown && (
                    <section className="report-section report-terms">
                        <div className="report-section__heading">
                            <div>
                                <div className="ff-mono report-section__eyebrow">
                                    Legal
                                </div>
                                <h2 className="ff-serif report-section__title">
                                    Terms &amp; Conditions
                                </h2>
                            </div>
                        </div>
                        <div className="report-terms__body">
                            <ReactMarkdown>{termsMarkdown}</ReactMarkdown>
                        </div>
                    </section>
                )}
            </div>
        </>
    );
};

export default RFQReport;
